import {
    Box,
    Grid,
    Button,
    TextareaAutosize,
    Typography,
    FormControlLabel,
    Checkbox,
    TextField,
    IconButton,
    Tooltip
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { useTranslation } from 'react-i18next';
import useCreateVisita from './hooks/useCreateVisita';
import SectionFormBox from '../../../components/SectionFormBox/SectionFormBox';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { useGetPreventorsQuery, useGetTiposVisitasQuery } from '../../../services/visitasApi';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import EstablecimientosSelector from '../components/EstablecimientosSelector';
import EstablecimientosSeleccionadosDrawer from '../components/EstablecimientosSeleccionadosDrawer';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import CreateVisitasFilters from './components/CreateVisitasFilters';
import EstablecimientosSeleccionados from '../components/EstablecimientosSeleccionados';
import AutocompleteConChips from '../../../components/layout/SelectConChips';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatFullName } from '../../../utils/common';
import { useGetProvidersQuery } from '../../../services/providersApi';
import { useEffect, useMemo, useState } from 'react';
import { useGetEmpresaQuery } from '../../../services/companyApi';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../consts/visitTypeDisplayNames';
import { soloPreventoresActivos } from '../utils/preventoresActivos';
import { normalizeEstablishmentCardData } from './utils/establecimientoCard';

const getOccurrenceLocationDisplayData = (
    occurrenceLocation?: any,
    claimNumber?: string | number | null,
    roamNumber?: string | number | null,
    patientName?: string | null,
    patientCuil?: string | number | null
) => {
    if (!occurrenceLocation) return null;

    const streetLine = [occurrenceLocation.calle, occurrenceLocation.altura]
        .filter(Boolean)
        .join(' ')
        .trim();
    const floorLine = [
        occurrenceLocation.piso ? `Piso ${occurrenceLocation.piso}` : '',
        occurrenceLocation.depto ? `Depto ${occurrenceLocation.depto}` : ''
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

    const localityName = occurrenceLocation.localidad || occurrenceLocation.localidadData?.nombre;
    const provinceName =
        occurrenceLocation.provincia || occurrenceLocation.localidadData?.provincia?.nombre;
    const postalCode =
        occurrenceLocation.codigoPostal || occurrenceLocation.localidadData?.codigoPostal;

    const hasUsefulData = Boolean(
        occurrenceLocation.calle ||
        occurrenceLocation.altura ||
        occurrenceLocation.piso ||
        occurrenceLocation.depto ||
        postalCode ||
        localityName ||
        provinceName
    );

    if (!hasUsefulData) return null;

    return {
        claimNumber: claimNumber || 'N/A',
        roamNumber: roamNumber || 'N/A',
        patientName: patientName || 'N/A',
        patientCuil: patientCuil || 'N/A',
        address: [streetLine, floorLine].filter(Boolean).join(' - ') || 'N/A',
        locality: localityName || 'N/A',
        province: provinceName || 'N/A',
        postalCode: postalCode || 'N/A'
    };
};

const shouldShowEmployeeCard = (patientName?: string | null) => {
    if (!patientName) return false;

    const normalizedNameParts = patientName
        .trim()
        .split(/\s+/)
        .map((part) => part.toUpperCase());

    if (normalizedNameParts.length === 0) return false;

    return !normalizedNameParts.every((part) => part === 'N/A');
};

const OBSERVATION_MAX_LENGTH = 4000;

function CreateVisita() {
    const { t } = useTranslation('visitas');
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = location?.state?.mode === 'edit';
    const isReadOnlyMode = location?.state?.mode === 'view';
    const selectedVisitaFromState = location?.state?.selectedVisita || null;
    const selectedEstablishmentFromDetail =
        location?.state?.selectedEstablishment ||
        selectedVisitaFromState?.establecimiento ||
        null;
    const linkedSiniestroId = location?.state?.idSiniestro || null;
    const linkedEstablishmentId =
        Number(location?.state?.idEstablecimiento || selectedEstablishmentFromDetail?.id) || null;
    const linkedSiniestroNumber = location?.state?.nroSiniestro || null;
    const linkedRoamNumber = location?.state?.nroRoam || null;
    const linkedPatientName = location?.state?.patientName || null;
    const linkedPatientCuil = location?.state?.patientCuil || null;
    const occurrenceLocation = location?.state?.occurrenceLocation || null;
    const occurrenceDisplayData = getOccurrenceLocationDisplayData(
        occurrenceLocation,
        linkedSiniestroNumber || linkedSiniestroId,
        linkedRoamNumber,
        linkedPatientName,
        linkedPatientCuil
    );
    const showEmployeeCard = shouldShowEmployeeCard(occurrenceDisplayData?.patientName);
    const {
        step,
        setStep,
        handleGoBack,
        formData,
        handleChange,
        handleTipoVisitaChange,
        contracts,
        provinces,
        cities,
        organisms,
        handleContractSelection,
        establishments,
        selectedEstablishmentsDetails,
        handleSelectEstablishment,
        handleSelectEstablishments,
        handleClearSelection,
        handleSubmit,
        isFetchingContracts,
        loadingValidacionRGRL,
        handleFiltersEstablecimientosChange,
        filtersEstablecimientos,
        organizationsOptions,
        ciiuCodesOptions,
        isFetchingCiiuCodes,
        cleanEstablecimientosFilters,
        isFetchingEstablishments,
        isLoadingEstablishments,
        establecimientosPagination,
        establecimientosTotalCount,
        handleChangeEstablecimientosPage,
        handleChangeEstablecimientosRowsPerPage,
        isNextDisabled,
        isCreatingVisita,
        fechaLimiteEditadaManualmente,
        mostrarDistancia,
        visitasPorEstablecimiento,
        isFetchingVisitasEstablecimientos,
        fechaMinimaSeguimiento,
        fechaLimiteAnteriorSeguimiento,
        establecimientosIncompatibles,
        tiposVisitaIncompatibles,
        hayVisitasPendientesDeCargar
    } = useCreateVisita(
        selectedEstablishmentFromDetail,
        location,
        selectedVisitaFromState,
        isEditMode,
        isReadOnlyMode
    );
    const { data: linkedEstablishmentData } = useGetEmpresaQuery(linkedEstablishmentId, {
        skip: !linkedSiniestroId || !linkedEstablishmentId
    });

    const selectedLinkedEstablishment = useMemo(() => {
        if (!linkedEstablishmentId) return null;

        const establishmentFromList = establishments.find(
            (establishment: any) => establishment.id === linkedEstablishmentId
        );

        return normalizeEstablishmentCardData(
            linkedEstablishmentData || establishmentFromList,
            selectedEstablishmentFromDetail || { id: linkedEstablishmentId }
        );
    }, [
        establishments,
        linkedEstablishmentData,
        linkedEstablishmentId,
        selectedEstablishmentFromDetail
    ]);

    const selectedEditEstablishment = useMemo(() => {
        if (!isEditMode && !isReadOnlyMode) return null;

        const source = selectedEstablishmentFromDetail || selectedVisitaFromState?.establecimiento;

        if (!source && !linkedEstablishmentId) return null;

        const normalized = normalizeEstablishmentCardData(source, {
            id: linkedEstablishmentId || selectedVisitaFromState?.idEstablecimiento,
            nombre:
                selectedVisitaFromState?.nombreEstablecimiento ||
                selectedEstablishmentFromDetail?.nombre ||
                'Establecimiento',
            localidad: source?.direccion?.localidad || null,
            postalCode:
                source?.direccion?.localidad?.codigoPostal ||
                source?.postalCode ||
                source?.codigoPostal ||
                'N/A',
            estado: source?.estado,
            direccion: {
                calle: source?.direccion?.calle ?? source?.calle ?? '',
                altura: source?.direccion?.altura ?? source?.altura ?? '',
                departamento: source?.direccion?.departamento ?? source?.departamento ?? null
            }
        });

        return {
            ...(source || {}),
            ...normalized,
            id: normalized?.id,
            nombre: normalized?.nombre,
            localidad: normalized?.localidad,
            postalCode: normalized?.postalCode,
            estado: normalized?.estado,
            direccion: {
                ...(source?.direccion || {}),
                ...(normalized?.direccion || {}),
                localidad:
                    source?.direccion?.localidad ||
                    source?.localidad ||
                    normalized?.localidad ||
                    null
            }
        };
    }, [
        isEditMode,
        isReadOnlyMode,
        linkedEstablishmentId,
        selectedEstablishmentFromDetail,
        selectedVisitaFromState
    ]);

    const { data: tiposVisitas, isLoading: loadingTiposVisitas } = useGetTiposVisitasQuery(null);

    const tiposVisitasOptions = (tiposVisitas?.data || [])
        .filter((tipo: any) =>
            linkedSiniestroId ? tipo.requiereSiniestro === true : tipo.requiereSiniestro !== true
        )
        .map((tipo: any) => ({
            nombre: VISIT_TYPE_DISPLAY_NAME_OVERRIDES[tipo.id] ?? `${tipo.descripcion}`.toUpperCase(),
            id: tipo.id,
            value: tipo.id
        }));

    const tiposVisitasOptionsWithSelected = [
        ...formData.idTipoVisita.filter(
            (selectedTipoVisita: any) =>
                selectedTipoVisita &&
                !tiposVisitasOptions.some(
                    (tipoVisita: any) =>
                        String(tipoVisita.id) === String(selectedTipoVisita.id)
                )
        ),
        ...tiposVisitasOptions
    ];

    const [selectedProvider, setSelectedProvider] = useState<any>(null);
    const [showSelectedEstablishmentsDrawer, setShowSelectedEstablishmentsDrawer] = useState(false);

    const { data: providersData, isFetching: isFetchingProviders } = useGetProvidersQuery({
        activo: true,
        take: 999999,
        skip: 0
    });

    const { data: preventorsData, isFetching: isFetchingPreventors } = useGetPreventorsQuery({
        ...(selectedProvider?.id && { idProveedor: selectedProvider.id }),
        take: 999999,
        skip: 0
    });

    const providersOptions = providersData?.data?.data || [];

    const matchedProvider = formData.idProveedor
        ? providersOptions.find(
              (provider: any) => String(provider.id) === String(formData.idProveedor)
          ) || null
        : null;

    const providerNotFound = !isFetchingProviders && !!formData.idProveedor && !matchedProvider;

    useEffect(() => {
        setSelectedProvider(matchedProvider);
    }, [matchedProvider]);

    // Solo preventores activos (SMP-428); el preventor actual de la visita se agrega abajo aunque
    // haya quedado inactivo, para que se siga viendo al editar.
    const preventoresOptions =
        soloPreventoresActivos<any>(preventorsData?.data).map((preventor: any) => ({
            nombre: formatFullName(preventor.nombre, preventor.apellido),
            id: preventor.idUsuario,
            value: preventor.idUsuario
        }));

    const preventoresOptionsWithSelected =
        formData.idPreventor &&
        !preventoresOptions.some(
            (preventor: any) => String(preventor.id) === String(formData.idPreventor?.id)
        )
            ? [formData.idPreventor, ...preventoresOptions]
            : preventoresOptions;

    const handleProviderChange = (value: any) => {
        setSelectedProvider(value || null);
        handleChange({ target: { name: 'idProveedor', value: value?.id || null } });
        handleChange({ target: { name: 'idPreventor', value: null } });
    };

    if (!isEditMode && !isReadOnlyMode && step === 1 && !linkedSiniestroId) {
        return (
            <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <CreateVisitasFilters
                        formData={formData}
                        provinces={provinces}
                        cities={cities}
                        contracts={contracts}
                        organisms={organisms}
                        filtersEstablecimientos={filtersEstablecimientos}
                        organizacionesOptions={organizationsOptions}
                        ciiuOptions={ciiuCodesOptions}
                        isFetchingCiiuOptions={isFetchingCiiuCodes}
                        isFetchingContracts={isFetchingContracts}
                        handleChange={handleChange}
                        handleFiltersEstablecimientosChange={handleFiltersEstablecimientosChange}
                        handleContractSelection={handleContractSelection}
                        cleanEstablecimientosFilters={cleanEstablecimientosFilters}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {formData.idEstablecimientos.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {t('newVisit.selectedEstablishmentsCount', {
                                    count: formData.idEstablecimientos.length
                                })}
                            </Typography>
                            <Tooltip title={t('newVisit.seeSelectedEstablishmentsTooltip')}>
                                <IconButton
                                    size="small"
                                    onClick={() => setShowSelectedEstablishmentsDrawer(true)}
                                >
                                    <AssignmentOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <EstablecimientosSelector
                        establishments={establishments}
                        selectedEstablishments={formData.idEstablecimientos}
                        handleSelectEstablishment={handleSelectEstablishment}
                        handleSelectEstablishments={handleSelectEstablishments}
                        handleClearSelection={handleClearSelection}
                        isLoading={isFetchingEstablishments || isLoadingEstablishments}
                        page={establecimientosPagination.page}
                        rowsPerPage={establecimientosPagination.rowsPerPage}
                        totalCount={establecimientosTotalCount}
                        onChangePage={handleChangeEstablecimientosPage}
                        onChangeRowsPerPage={handleChangeEstablecimientosRowsPerPage}
                    />
                    <EstablecimientosSeleccionadosDrawer
                        open={showSelectedEstablishmentsDrawer}
                        setOpen={setShowSelectedEstablishmentsDrawer}
                        establishments={selectedEstablishmentsDetails}
                        onRemove={handleSelectEstablishment}
                    />
                </Box>

                <Grid container justifyContent="space-between" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={() =>
                            linkedSiniestroId
                                ? navigate('/backoffice/siniestros/detalle', {
                                      state: { siniestroId: linkedSiniestroId }
                                  })
                                : window.history.back()
                        }
                    >
                        {t('buttons.cancel')}
                    </Button>

                    <Button
                        variant="contained"
                        disabled={formData.idEstablecimientos.length === 0}
                        onClick={() => setStep(2)}
                    >
                        {t('buttons.nextCount', { count: formData.idEstablecimientos.length })}
                    </Button>
                </Grid>

                <BackdropComponent loading={loadingValidacionRGRL || isCreatingVisita} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 65px)' }}>
            <Box>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <SectionFormBox
                            className="1"
                            title={
                                linkedRoamNumber
                                    ? t('newVisit.visitDetailsWithClaim', {
                                          claim: t('newVisit.roamNumber', {
                                              number: linkedRoamNumber
                                          })
                                      })
                                    : t('newVisit.visitDetails')
                            }
                        >
                            <Grid item xs={6}>
                                <AutocompleteConChips
                                    value={formData.idTipoVisita}
                                    setValue={handleTipoVisitaChange}
                                    options={tiposVisitasOptionsWithSelected}
                                    size="small"
                                    name="idTipoVisita"
                                    label={`${t('filters.visitType')} *`}
                                    loading={loadingTiposVisitas}
                                    allowMultipleSelection={true}
                                    disabled={isReadOnlyMode}
                                    isOptionIncompatible={(option: any) =>
                                        tiposVisitaIncompatibles.has(option?.id)
                                    }
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                    <DatePicker
                                        label={`${t('filters.deadlineDate')} *`}
                                        minDate={
                                            fechaMinimaSeguimiento?.isAfter(dayjs(), 'day')
                                                ? fechaMinimaSeguimiento
                                                : dayjs()
                                        }
                                        value={formData.fechaLimite}
                                        disabled={isReadOnlyMode}
                                        onChange={(newDate) =>
                                            handleChange({
                                                target: { name: 'fechaLimite', value: newDate }
                                            })
                                        }
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: fechaLimiteAnteriorSeguimiento,
                                                helperText: fechaLimiteAnteriorSeguimiento
                                                    ? t(
                                                          'alerts.seguimientoVerosimilitud.fechaAnteriorSeguimiento'
                                                      )
                                                    : linkedSiniestroId &&
                                                        formData.fechaLimite &&
                                                        !fechaLimiteEditadaManualmente
                                                      ? 'Fecha sugerida según tipo de siniestro'
                                                      : undefined
                                            }
                                        }}
                                        format="DD/MM/YYYY"
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {mostrarDistancia && (
                                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.distanciaMayor100km === true}
                                                disabled={isReadOnlyMode}
                                                onChange={(e) =>
                                                    handleChange({
                                                        target: {
                                                            name: 'distanciaMayor100km',
                                                            value: e.target.checked
                                                        }
                                                    })
                                                }
                                            />
                                        }
                                        label="¿Distancia >100 km?"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextareaAutosize
                                    minRows={16}
                                    name="observaciones"
                                    value={formData.observaciones}
                                    disabled={isReadOnlyMode}
                                    onChange={handleChange}
                                    placeholder={t('filters.observations') ?? ''}
                                    style={{ width: '99%', padding: '8px', marginLeft: '4px' }}
                                    maxLength={OBSERVATION_MAX_LENGTH}
                                />
                                <Typography
                                    variant="caption"
                                    color={
                                        formData.observaciones.length > OBSERVATION_MAX_LENGTH
                                            ? 'error'
                                            : 'textSecondary'
                                    }
                                    sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                                >
                                    {formData.observaciones.length}/{OBSERVATION_MAX_LENGTH}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                {providerNotFound ? (
                                    <TextField
                                        value="-"
                                        size="small"
                                        fullWidth
                                        disabled
                                        label={`${t('newVisit.providerFieldLabel')}`}
                                    />
                                ) : (
                                    <AutocompleteComponent
                                        value={selectedProvider}
                                        options={providersOptions}
                                        size="small"
                                        name="provider"
                                        label={`${t('newVisit.providerFieldLabel')}`}
                                        loading={isFetchingProviders}
                                        onChange={handleProviderChange}
                                        templateLabel={(option: any) => option?.razonSocial || ''}
                                        clearable
                                        disabled={isReadOnlyMode}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <AutocompleteComponent
                                    value={formData.idPreventor}
                                    options={preventoresOptionsWithSelected}
                                    size="small"
                                    name="idPreventor"
                                    label={`${t('filters.preventor')}`}
                                    loading={isFetchingPreventors}
                                    onChange={(value: any) =>
                                        handleChange({ target: { name: 'idPreventor', value } })
                                    }
                                    clearable
                                    disabled={isReadOnlyMode}
                                />
                            </Grid>
                        </SectionFormBox>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        {linkedSiniestroId ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {occurrenceDisplayData && showEmployeeCard && (
                                    <Box
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor: '#fff',
                                            p: 2,
                                            boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)',
                                            width: '100%',
                                            maxHeight: 500,
                                            overflowY: 'auto'
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {occurrenceDisplayData.patientName}
                                            </Typography>

                                            <Box sx={{ pl: 0.5, mt: 0.5 }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {`CUIL: ${occurrenceDisplayData.patientCuil}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ mb: 1.5, fontWeight: 700, color: '#384A52' }}
                                    >
                                        {t('newVisit.selectedOccurrenceLocation')}
                                    </Typography>

                                    <Box
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor: '#fff',
                                            p: 2,
                                            boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)',
                                            width: '100%'
                                        }}
                                    >
                                        {occurrenceDisplayData && (
                                            <Box sx={{ width: '100%' }}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {`Dirección: ${occurrenceDisplayData.address}`}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {`Localidad: ${occurrenceDisplayData.locality} (${occurrenceDisplayData.postalCode})`}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {`Provincia: ${occurrenceDisplayData.province}`}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>

                                {selectedLinkedEstablishment && (
                                    <EstablecimientosSeleccionados
                                        establishments={[selectedLinkedEstablishment]}
                                        selectedEstablishments={[selectedLinkedEstablishment.id]}
                                        title="Establecimiento del empleado"
                                        visitasPorEstablecimiento={visitasPorEstablecimiento}
                                        isLoadingVisitas={isFetchingVisitasEstablecimientos}
                                        establecimientosIncompatibles={establecimientosIncompatibles}
                                        tiposVisitaIncompatibles={tiposVisitaIncompatibles}
                                        haySeleccionTipoVisita={formData.idTipoVisita.length > 0}
                                    />
                                )}
                            </Box>
                        ) : (
                            <>
                                {(isEditMode || isReadOnlyMode) && selectedEditEstablishment ? (
                                    <EstablecimientosSeleccionados
                                        establishments={establishments}
                                        selectedEstablishments={formData.idEstablecimientos}
                                        selectedEstablishmentsData={[selectedEditEstablishment]}
                                        title={t('newVisit.selectedEstablishments')}
                                        visitasPorEstablecimiento={visitasPorEstablecimiento}
                                        isLoadingVisitas={isFetchingVisitasEstablecimientos}
                                        establecimientosIncompatibles={establecimientosIncompatibles}
                                        tiposVisitaIncompatibles={tiposVisitaIncompatibles}
                                        haySeleccionTipoVisita={formData.idTipoVisita.length > 0}
                                    />
                                ) : (
                                    <EstablecimientosSeleccionados
                                        establishments={establishments}
                                        selectedEstablishments={formData.idEstablecimientos}
                                        selectedEstablishmentsData={selectedEstablishmentsDetails}
                                        onRemove={(id) => handleSelectEstablishment(id)}
                                        onRemoveMultiple={(ids) => handleSelectEstablishments(ids, false)}
                                        visitasPorEstablecimiento={visitasPorEstablecimiento}
                                        isLoadingVisitas={isFetchingVisitasEstablecimientos}
                                        establecimientosIncompatibles={establecimientosIncompatibles}
                                        tiposVisitaIncompatibles={tiposVisitaIncompatibles}
                                        haySeleccionTipoVisita={formData.idTipoVisita.length > 0}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>

            <Grid
                container
                justifyContent="flex-start"
                sx={{ mt: 'auto' }}
            >
                {isReadOnlyMode ? (
                    <Button variant="outlined" onClick={handleGoBack}>
                        {t('buttons.back')}
                    </Button>
                ) : isEditMode ? (
                    <Button variant="outlined" onClick={handleGoBack}>
                        {t('buttons.back')}
                    </Button>
                ) : linkedSiniestroId ? (
                    <Button
                        variant="outlined"
                        onClick={() =>
                            navigate('/backoffice/siniestros/detalle', {
                                state: { siniestroId: linkedSiniestroId }
                            })
                        }
                        >
                        {t('buttons.back')}
                    </Button>
                ) : (
                    !selectedEstablishmentFromDetail && (
                        <Button variant="outlined" onClick={handleGoBack}>
                            {t('buttons.back')}
                        </Button>
                    )
                )}

                {isReadOnlyMode ? null : isEditMode ? (
                    <Button
                        variant="contained"
                        disabled={
                            isNextDisabled ||
                            establecimientosIncompatibles.size > 0 ||
                            hayVisitasPendientesDeCargar
                        }
                        onClick={handleSubmit}
                        sx={{ ml: 'auto' }}
                    >
                        {t('buttons.save')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        disabled={
                            establecimientosIncompatibles.size > 0 || hayVisitasPendientesDeCargar
                        }
                        onClick={handleSubmit}
                        sx={{ ml: 'auto' }}
                    >
                        {t('buttons.generatePlural')}
                    </Button>
                )}
            </Grid>

            <BackdropComponent
                loading={loadingValidacionRGRL || isCreatingVisita || isFetchingVisitasEstablecimientos}
            />
        </Box>
    );
}

export default CreateVisita;
